import {Injectable} from '@angular/core';

/** Shape of /assets/config/app-config.json — edited per deployment, no rebuild needed. */
interface ConfigJson {
    /** PMCC (cost control) backend origin, e.g. "https://api-dev.example.com". Empty = same origin (dev proxy). */
    apiBaseUrl?: string;
    /** FFM (MDM) backend origin: login, change password and Employee Master only. Empty = `apiBaseUrl`. */
    ffmApiBaseUrl?: string;
    /** Audit log backend origin. Empty = `apiBaseUrl`. */
    auditLogApiBaseUrl?: string;
    environmentName: string;
    /** Minutes of inactivity before the session-expired dialog. 0 or missing = disabled. */
    idleTimeoutMinutes?: number;
    /** Show the Single Sign-On button on the login page. */
    enableSso?: boolean;
    /** Restrict pages/buttons by the login `Permission[]`. false = every module is allowed. */
    enforcePermissions?: boolean;
}

/** Backend a request goes to: `pmcc` = `apiBaseUrl`, `ffm` = `ffmApiBaseUrl`, `auditLog` = `auditLogApiBaseUrl`. */
export type ApiService = 'pmcc' | 'ffm' | 'auditLog';

export interface RuntimeConfig {
    apiBaseUrl: string;
    ffmApiBaseUrl: string;
    auditLogApiBaseUrl: string;
    environmentName: string;
    idleTimeoutMinutes: number;
    enableSso: boolean;
    enforcePermissions: boolean;
}

@Injectable({providedIn: 'root'})
export class AppConfigService {
    config!: RuntimeConfig;

    load(): Promise<void> {
        return fetch('/assets/config/app-config.json')
            .then(r => {
                if (!r.ok) {
                    throw new Error(`[AppConfig] Failed to load app-config.json: HTTP ${r.status}`);
                }
                return r.json().then((c: ConfigJson) => {
                    if (!c.environmentName || (c.apiBaseUrl === undefined && c.ffmApiBaseUrl === undefined)) {
                        throw new Error('[AppConfig] app-config.json is missing required fields');
                    }
                    this.config = {
                        apiBaseUrl: trimSlash(c.apiBaseUrl ?? ''),
                        ffmApiBaseUrl: trimSlash(c.ffmApiBaseUrl ?? ''),
                        auditLogApiBaseUrl: trimSlash(c.auditLogApiBaseUrl ?? ''),
                        environmentName: c.environmentName,
                        idleTimeoutMinutes: c.idleTimeoutMinutes ?? 0,
                        enableSso: c.enableSso ?? false,
                        enforcePermissions: c.enforcePermissions ?? true,
                    };
                });
            });
    }

    /** Base URL for a backend: `ffm` / `auditLog` use their own URL (falling back to `apiBaseUrl`), `pmcc` uses `apiBaseUrl`. */
    baseUrl(service: ApiService = 'pmcc'): string {
        if (service === 'ffm') return this.config.ffmApiBaseUrl || this.config.apiBaseUrl;
        if (service === 'auditLog') return this.config.auditLogApiBaseUrl || this.config.apiBaseUrl;
        return this.config.apiBaseUrl;
    }

    /** All absolute backend origins, used to decide which requests get the auth token. */
    apiOrigins(): string[] {
        return [this.config.apiBaseUrl, this.config.ffmApiBaseUrl, this.config.auditLogApiBaseUrl].filter(Boolean);
    }
}

function trimSlash(url: string): string {
    return url.replace(/\/+$/, '');
}
